import React, { useState, useEffect } from 'react';
import {
    getAnwendungen,
    getProzesse,
    createAnwendung,
    updateAnwendung,
    deleteAnwendung,
    getAnwendungsanalysen,
    createAnwendungsanalyse,
    updateAnwendungsanalyse,
} from '../../../../../services/schutzbedarfsanalyseService';
import AnwendungModal from './AnwendungModal';
import AnwendungsanalyseModal from './AnwendungsanalyseModal';
import './AnwendungenSection.css';

const AnwendungenSection = () => {
    const [anwendungen, setAnwendungen] = useState([]);
    const [prozesse, setProzesse] = useState([]);
    const [analysen, setAnalysen] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedAnwendungen, setExpandedAnwendungen] = useState(new Set());

    const [showModal, setShowModal] = useState(false);
    const [editingAnwendung, setEditingAnwendung] = useState(null);

    const [showAnalyseModal, setShowAnalyseModal] = useState(false);
    const [selectedAnwendung, setSelectedAnwendung] = useState(null);
    const [editingAnalyse, setEditingAnalyse] = useState(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        setError(null);

        const [anwendungenResult, prozesseResult, analysenResult] = await Promise.all([
            getAnwendungen(),
            getProzesse(),
            getAnwendungsanalysen(),
        ]);

        if (anwendungenResult.success) {
            setAnwendungen(anwendungenResult.data);
        } else {
            setError(anwendungenResult.error);
        }

        if (prozesseResult.success) {
            setProzesse(prozesseResult.data);
        }

        if (analysenResult.success) {
            setAnalysen(analysenResult.data);
        }

        setLoading(false);
    };

    const toggleAnwendung = (anwendungId) => {
        const newExpanded = new Set(expandedAnwendungen);
        if (newExpanded.has(anwendungId)) {
            newExpanded.delete(anwendungId);
        } else {
            newExpanded.add(anwendungId);
        }
        setExpandedAnwendungen(newExpanded);
    };

    const handleCreateAnwendung = () => {
        setEditingAnwendung(null);
        setShowModal(true);
    };

    const handleEditAnwendung = (anwendung, e) => {
        e.stopPropagation();
        setEditingAnwendung(anwendung);
        setShowModal(true);
    };

    const handleDeleteAnwendung = async (anwendung, e) => {
        e.stopPropagation();
        if (!window.confirm(`Anwendung "${anwendung.anwendung_name}" wirklich löschen?`)) {
            return;
        }

        const result = await deleteAnwendung(anwendung.anwendung_id);
        if (result.success) {
            loadData();
        } else {
            alert('Fehler beim Löschen: ' + result.error);
        }
    };

    const handleSave = async (anwendungData) => {
        let result;

        if (editingAnwendung) {
            result = await updateAnwendung(editingAnwendung.anwendung_id, anwendungData);
        } else {
            result = await createAnwendung(anwendungData);
        }

        if (result.success) {
            setShowModal(false);
            setEditingAnwendung(null);
            loadData();
        } else {
            alert('Fehler beim Speichern: ' + result.error);
        }
    };

    const handleAnalyseAnwendung = (anwendung, e) => {
        e.stopPropagation();
        const existingAnalyse = analysen.find(a => a.anwendung_id === anwendung.anwendung_id);

        setSelectedAnwendung(anwendung);
        setEditingAnalyse(existingAnalyse || null);
        setShowAnalyseModal(true);
    };

    const handleSaveAnalyse = async (analyseData) => {
        let result;

        if (editingAnalyse) {
            result = await updateAnwendungsanalyse(editingAnalyse.analyse_id, analyseData);
        } else {
            result = await createAnwendungsanalyse({
                ...analyseData,
                anwendung_id: selectedAnwendung.anwendung_id,
            });
        }

        if (result.success) {
            setShowAnalyseModal(false);
            setSelectedAnwendung(null);
            setEditingAnalyse(null);
            loadData();
        } else {
            alert('Fehler beim Speichern: ' + result.error);
        }
    };

    const getProzessName = (prozessId) => {
        const prozess = prozesse.find(p => p.prozess_id === prozessId);
        return prozess ? prozess.prozess_name : '-';
    };

    const getAnalyseForAnwendung = (anwendungId) => {
        return analysen.find(a => a.anwendung_id === anwendungId);
    };

    const getSchutzniveauBadge = (level) => {
        if (!level) return <span className="badge badge-none">Nicht bewertet</span>;

        const config = {
            'sehr_hoch': { label: 'A2 C4 I3 N3', color: 'badge-sehr-hoch' },
            'hoch': { label: 'A2 C3 I3 N3', color: 'badge-hoch' },
            'normal': { label: 'A2 C1 I2 N2', color: 'badge-normal' },
            'gering': { label: 'A2 C1N3', color: 'badge-gering' },
        };

        const badge = config[level] || { label: 'Nicht bewertet', color: 'badge-none' };

        return <span className={`badge ${badge.color}`}>{badge.label}</span>;
    };

    const getStatusBadge = (analyse) => {
        if (!analyse) return <span className="badge badge-gray">Nicht analysiert</span>;

        const config = {
            'entwurf': { label: 'Entwurf', color: 'badge-gray' },
            'in_bearbeitung': { label: 'In Bearbeitung', color: 'badge-yellow' },
            'abgeschlossen': { label: 'Abgeschlossen', color: 'badge-green' },
        };

        const status = config[analyse.status] || { label: 'Unbekannt', color: 'badge-gray' };

        return <span className={`badge ${status.color}`}>{status.label}</span>;
    };

    const groupAnwendungenByCategory = () => {
        const grouped = {};
        anwendungen.forEach(anwendung => {
            const category = anwendung.anwendung_kategorie || 'Ohne Kategorie';
            if (!grouped[category]) {
                grouped[category] = [];
            }
            grouped[category].push(anwendung);
        });
        return grouped;
    };

    if (loading) {
        return (
            <div className="anwendungen-section">
                <div className="loading-state">Lade Anwendungen...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="anwendungen-section">
                <div className="error-state">
                    <p>Fehler beim Laden: {error}</p>
                    <button onClick={loadData} className="btn-retry">Erneut versuchen</button>
                </div>
            </div>
        );
    }

    const groupedAnwendungen = groupAnwendungenByCategory();

    return (
        <div className="anwendungen-section">
            <div className="section-header-bar">
                <div className="section-title">
                    <h2>IT-Anwendungen</h2>
                </div>
                <button onClick={handleCreateAnwendung} className="btn-export">
                    + Neue Anwendung
                </button>
            </div>

            {anwendungen.length === 0 ? (
                <div className="empty-state">
                    <h3>Keine Anwendungen vorhanden</h3>
                    <p>Erstellen Sie Ihre erste IT-Anwendung</p>
                    <button onClick={handleCreateAnwendung} className="btn-primary">
                        Anwendung erstellen
                    </button>
                </div>
            ) : (
                <div className="anwendungen-table-container">
                    <table className="anwendungen-table">
                        <thead>
                            <tr>
                                <th className="col-bezeichnung">Bezeichnung</th>
                                <th className="col-verantwortung">Verantwortung</th>
                                <th className="col-prozess">Zugeordneter Prozess</th>
                                <th className="col-schutzbedarf">Schutzbedarf</th>
                                <th className="col-status">Status</th>
                                <th className="col-actions">Aktionen</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(groupedAnwendungen).map(([category, categoryAnwendungen]) => (
                                <React.Fragment key={category}>
                                    <tr className="category-row">
                                        <td colSpan="6" className="category-header">
                                            <strong>{category}</strong>
                                        </td>
                                    </tr>
                                    {categoryAnwendungen.map(anwendung => {
                                        const analyse = getAnalyseForAnwendung(anwendung.anwendung_id);
                                        const isExpanded = expandedAnwendungen.has(anwendung.anwendung_id);

                                        return (
                                            <React.Fragment key={anwendung.anwendung_id}>
                                                <tr 
                                                    className="anwendung-row"
                                                    onClick={() => toggleAnwendung(anwendung.anwendung_id)}
                                                >
                                                    <td className="col-bezeichnung">
                                                        <div className="bezeichnung-cell">
                                                            <span className="expand-icon">
                                                                {isExpanded ? '▼' : '▶'}
                                                            </span>
                                                            <span className="anwendung-icon">💻</span>
                                                            <span className="anwendung-name">{anwendung.anwendung_name}</span>
                                                            {!anwendung.ist_relevant && (
                                                                <span className="badge badge-not-relevant" title={anwendung.nicht_relevant_begruendung || 'Nicht relevant'}>
                                                                    Nicht relevant
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="col-verantwortung">
                                                        <span className="person-icon">👤</span>
                                                        {anwendung.anwendung_verantwortlicher || '-'}
                                                    </td>
                                                    <td className="col-prozess">
                                                        {getProzessName(anwendung.prozess_id)}
                                                    </td>
                                                    <td className="col-schutzbedarf">
                                                        {getSchutzniveauBadge(anwendung.gesamtschutzbedarf)}
                                                    </td>
                                                    <td className="col-status">
                                                        {getStatusBadge(analyse)}
                                                    </td>
                                                    <td className="col-actions">
                                                        <button 
                                                            className="btn-table btn-analyze"
                                                            onClick={(e) => handleAnalyseAnwendung(anwendung, e)}
                                                        >
                                                            Analysieren
                                                        </button>
                                                        <button 
                                                            className="btn-table btn-edit"
                                                            onClick={(e) => handleEditAnwendung(anwendung, e)}
                                                        >
                                                            Bearbeiten
                                                        </button>
                                                        <button 
                                                            className="btn-table btn-delete"
                                                            onClick={(e) => handleDeleteAnwendung(anwendung, e)}
                                                        >
                                                            Löschen
                                                        </button>
                                                    </td>
                                                </tr>
                                                {isExpanded && (
                                                    <tr className="detail-row">
                                                        <td colSpan="6">
                                                            <div className="detail-content">
                                                                {!anwendung.ist_relevant && anwendung.nicht_relevant_begruendung && (
                                                                    <div className="detail-section relevanz-section">
                                                                        <strong>Begründung (Nicht relevant):</strong>
                                                                        <p className="nicht-relevant-text">{anwendung.nicht_relevant_begruendung}</p>
                                                                    </div>
                                                                )}
                                                                {anwendung.anwendung_beschreibung && (
                                                                    <div className="detail-section">
                                                                        <strong>Beschreibung:</strong>
                                                                        <p>{anwendung.anwendung_beschreibung}</p>
                                                                    </div>
                                                                )}
                                                                {analyse && (
                                                                    <div className="detail-section">
                                                                        <strong>Schutzziele:</strong>
                                                                        <div className="schutzziele-grid">
                                                                            <div>
                                                                                <span className="schutzziel-label">Vertraulichkeit:</span>
                                                                                <span className="schutzziel-value">{analyse.vertraulichkeit}</span>
                                                                            </div>
                                                                            <div>
                                                                                <span className="schutzziel-label">Integrität:</span>
                                                                                <span className="schutzziel-value">{analyse.integritaet}</span>
                                                                            </div>
                                                                            <div>
                                                                                <span className="schutzziel-label">Verfügbarkeit:</span>
                                                                                <span className="schutzziel-value">{analyse.verfuegbarkeit}</span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showModal && (
                <AnwendungModal
                    anwendung={editingAnwendung}
                    prozesse={prozesse}
                    onSave={handleSave}
                    onClose={() => {
                        setShowModal(false);
                        setEditingAnwendung(null);
                    }}
                />
            )}

            {showAnalyseModal && selectedAnwendung && (
                <AnwendungsanalyseModal
                    anwendung={selectedAnwendung}
                    analyse={editingAnalyse}
                    onSave={handleSaveAnalyse}
                    onClose={() => {
                        setShowAnalyseModal(false);
                        setSelectedAnwendung(null);
                        setEditingAnalyse(null);
                    }}
                />
            )}
        </div>
    );
};

export default AnwendungenSection;
